// ============================================================================
// Image helpers
// ============================================================================
// Table cells show 44px avatars but the stored objects are full-size scans
// (100-270 KB each). Rendering the original in a 44px box means a page of 25
// rows pulls several MB just for thumbnails. thumbUrl() asks Supabase Storage
// for a resized variant instead.
//
// Image transformation is not enabled on every plan, so callers should pair
// this with an onError fallback to the original URL (see thumbImgProps).
// ============================================================================

/** Convert a public Storage URL into a resized variant. Returns the input unchanged
 *  when it is not a Supabase public-object URL. */
export const thumbUrl = (url?: string | null, size = 88, quality = 65): string | null => {
  if (!url) return null;
  const marker = '/storage/v1/object/public/';
  const i = url.indexOf(marker);
  if (i === -1) return url; // not a Supabase object URL - leave it alone
  const base = url.slice(0, i);
  const objectPath = url.slice(i + marker.length);
  return `${base}/storage/v1/render/image/public/${objectPath}` +
         `?width=${size}&height=${size}&resize=cover&quality=${quality}`;
};

/** Props for a lazy, correctly-sized thumbnail that degrades to the original
 *  image if Storage transformations are unavailable. */
export const thumbImgProps = (url?: string | null, px = 44) => {
  const full = url || undefined;
  return {
    src: thumbUrl(url, px * 2) || undefined,   // 2x for retina
    loading: 'lazy' as const,
    decoding: 'async' as const,
    width: px,
    height: px,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (full && img.src !== full) img.src = full; // transformation unavailable
    },
    style: { width: px, height: px, objectFit: 'cover' as const, borderRadius: 4, background: 'rgba(0,0,0,0.04)' },
  };
};
